
export const GA4_EVENTS = {
  PAGE_VIEW: 'page_view',                 // fires automatically via SPA route hook
  SIGN_UP: 'sign_up',                   // shared — auth completion
  LOGIN: 'login',                     // shared — auth success
  GENERATE_LEAD: 'generate_lead',             // shared — scope started (client)
  CLIENT_REGISTERED: 'client_registered',
  VENDOR_REGISTERED: 'vendor_registered',
  PROFILE_COMPLETED: 'profile_completed',         // vendor or client — role in prop
  SCOPE_STARTED: 'scope_started',
  SCOPE_COMPLETED: 'scope_completed',
  VENDOR_PROFILE_COMPLETE: 'vendor_profile_complete',
  VENDOR_LOGIN: 'vendor_login',
  BEGIN_SCOPE: 'begin_scope',
  COMPLETE_SCOPE: 'complete_scope',
  DEMO_REQUESTED: 'demo_requested',
  CONTACT_FORM_SUBMITTED: 'contact_form_submitted',
  BROCHURE_DOWNLOADED: 'brochure_downloaded',
  NEWSLETTER_SUBSCRIBED: 'newsletter_subscribed',
  WHATSAPP_CLICKED: 'whatsapp_clicked',
} as const;

// ─── Client / customer journey (Mixpanel primary, GA4 secondary) ────
// Section headings mirror the "CLIENT DASHBOARD EVENTS" table in the doc.
export const CLIENT_EVENTS = {
  // Authentication
  CLIENT_REGISTERED: 'client_registered',
  CLIENT_EMAIL_VERIFIED: 'client_email_verified',
  CLIENT_LOGGED_IN: 'client_logged_in',
  CLIENT_LOGGED_OUT: 'client_logged_out',
  CLIENT_LOGIN_FAILED: 'client_login_failed',
  CLIENT_PROFILE_COMPLETED: 'client_profile_completed',

  // Profile
  CLIENT_PROFILE_STARTED: 'client_profile_started',
  CLIENT_COMPANY_DETAILS_ADDED: 'client_company_details_added',

  // Project scoping (highest priority per PM)
  SCOPE_STARTED: 'scope_started',
  AI_SCOPE_GENERATED: 'ai_scope_generated',
  SCOPE_EDITED: 'scope_edited',
  SCOPE_BUDGET_ADDED: 'scope_budget_added',
  SCOPE_TIMELINE_ADDED: 'scope_timeline_added',
  SCOPE_REFERENCE_UPLOADED: 'scope_reference_uploaded',
  SCOPE_SAVED: 'scope_saved',
  SCOPE_COMPLETED: 'scope_completed',
  SCOPE_ABANDONED: 'scope_abandoned',

  // Vendor discovery
  VENDOR_SEARCH: 'vendor_search',
  VENDOR_VIEWED: 'vendor_viewed',
  VENDOR_PORTFOLIO_VIEWED: 'vendor_portfolio_viewed',
  VENDOR_COMPARED: 'vendor_compared',
  VENDOR_INVITED: 'vendor_invited',

  // Proposal journey
  PROPOSAL_RECEIVED: 'proposal_received',
  PROPOSAL_VIEWED: 'proposal_viewed',
  PROPOSAL_DOWNLOADED: 'proposal_downloaded',
  PROPOSAL_COMPARED: 'proposal_compared',
  CLARIFICATION_ANSWERED: 'clarification_answered',
  PROPOSAL_RESUBMITTED: 'proposal_resubmitted',
  PROPOSAL_ACCEPTED: 'proposal_accepted',
  PROPOSAL_REJECTED: 'proposal_rejected',

  // TOE journey
  TOE_GENERATED: 'toe_generated',
  TOE_VIEWED: 'toe_viewed',
  TOE_SIGNED: 'toe_signed',

  // Project + payments
  PAYMENT_TERMS_REFLECTED: 'payment_terms_reflected',
  PAYMENT_RELEASED: 'payment_released',
  PAYMENT_MADE_TO_STUDIOIP: 'payment_made_to_studioip',
  PROJECT_STARTED: 'project_started',
  PROJECT_COMPLETED: 'project_completed',
  // Emitted only by the backend, from PUT /v1/projects/{id}/status, which
  // accepts exactly three values: In Progress, Completed, Abandoned. Declared
  // here so the two sides share one catalogue — a name that exists only as a
  // string literal in PHP is one typo away from a second, invisible event.
  PROJECT_ABANDONED: 'project_abandoned',
  REPEAT_PROJECT: 'repeat_project',
} as const;

// ─── Vendor journey (Mixpanel primary, GA4 secondary) ───────────────
export const VENDOR_EVENTS = {
  // Authentication
  VENDOR_REGISTERED: 'vendor_registered',
  VENDOR_EMAIL_VERIFIED: 'vendor_email_verified',
  VENDOR_LOGGED_IN: 'vendor_logged_in',
  VENDOR_LOGGED_OUT: 'vendor_logged_out',
  VENDOR_LOGIN_FAILED: 'vendor_login_failed',

  // Profile completion
  VENDOR_PROFILE_STARTED: 'vendor_profile_started',
  VENDOR_COMPANY_ADDED: 'vendor_company_added',
  VENDOR_SERVICES_ADDED: 'vendor_services_added',
  VENDOR_BIO_UPDATED: 'vendor_bio_updated',
  VENDOR_PORTFOLIO_UPLOADED: 'vendor_portfolio_uploaded',
  VENDOR_RATES_UPDATED: 'vendor_rates_updated',
  VENDOR_PROFILE_COMPLETED: 'vendor_profile_completed',

  // Vendor verification / AI readiness
  VENDOR_AI_NOT_BID_READY: 'vendor_ai_not_bid_ready',
  VENDOR_AI_BID_READY: 'vendor_ai_bid_ready',
  VENDOR_ONBOARDED: 'vendor_onboarded',

  // Opportunity flow
  PROJECT_INVITATION_RECEIVED: 'project_invitation_received',
  INVITATION_OPENED: 'invitation_opened',
  PROJECT_VIEWED: 'project_viewed',
  DID_NOT_BID: 'did_not_bid',
  BID_SENT: 'bid_sent',
  CLARIFICATION_ASKED: 'clarification_asked',

  // Proposal
  PROPOSAL_STARTED: 'proposal_started',
  PROPOSAL_SUBMITTED: 'proposal_submitted',
  PROPOSAL_EDITED: 'proposal_edited',
  PROPOSAL_ACCEPTED_VENDOR: 'vendor_proposal_accepted', // vendor-side view
  PROPOSAL_REJECTED_VENDOR: 'vendor_proposal_rejected',

  // Project + payments
  PROJECT_ASSIGNED: 'project_assigned',
  PROJECT_STARTED_VENDOR: 'vendor_project_started',
  PROJECT_DELIVERED: 'project_delivered',
  PAYMENT_RECEIVED: 'payment_received',
} as const;

// ─── Shared marketplace events ───────────────────────────────────────
// PM's doc: "These connect Client and Vendor journeys." Fired from
// whichever side triggered the transition; role is a property.
export const MARKETPLACE_EVENTS = {
  MARKETPLACE_VENDOR_INVITED: 'marketplace_vendor_invited',
  MARKETPLACE_PROPOSAL_SUBMITTED: 'marketplace_proposal_submitted',
  MARKETPLACE_PROPOSAL_ACCEPTED: 'marketplace_proposal_accepted',
  MARKETPLACE_TOE_SIGNED: 'marketplace_toe_signed',
  // Fired by the backend when a vendor is assigned. Named for the act, not
  // the outcome: it previously read `marketplace_project_started`, which
  // collided with `project_started` (status becoming In Progress). Two
  // differently-timed events both called "started" get conflated in reports,
  // and the mistake stays invisible until a funnel is quietly wrong.
  MARKETPLACE_VENDOR_ASSIGNED: 'marketplace_vendor_assigned',
  MARKETPLACE_PROJECT_STARTED: 'marketplace_project_started',
  MARKETPLACE_PROJECT_COMPLETED: 'marketplace_project_completed',
} as const;

// ─── Cross-cutting / infrastructure events ───────────────────────────
export const APP_EVENTS = {
  ROUTE_VIEWED: 'route_viewed',
  APP_ERROR: 'app_error',
  SESSION_EXPIRED: 'session_expired',
} as const;

// Union of every declared event name — the ONLY strings AnalyticsService.track() accepts.
export type EventName =
  | typeof GA4_EVENTS[keyof typeof GA4_EVENTS]
  | typeof CLIENT_EVENTS[keyof typeof CLIENT_EVENTS]
  | typeof VENDOR_EVENTS[keyof typeof VENDOR_EVENTS]
  | typeof MARKETPLACE_EVENTS[keyof typeof MARKETPLACE_EVENTS]
  | typeof APP_EVENTS[keyof typeof APP_EVENTS];
